import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Paper,
  Button,
  Alert,
  Container,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  Divider,
  Snackbar
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { filterFieldsByVisibility } from '../utils/fieldVisibility';
import { validateForm as validateFormFields, groupErrorsByField } from '../utils/fieldValidation';
import HtmlContentField from '../components/HtmlContentField';
import PhoneInput from '../components/PhoneInput';
import NotFound from '../components/NotFound';
import { sanitizeHtmlContent } from '../utils/htmlVariables';
import axios from 'axios';

interface FormField {
  id: string;
  name: string;
  type: string;
  label: string;
  placeholder?: string;
  required: boolean;
  options?: string[];
  order: number;
  conditional?: {
    dependsOn: string;
    showWhen: string[];
    hideWhen?: string[];
  };
  visibility?: {
    level?: 'public' | 'admin-only' | 'hidden';
    publicCanView?: boolean;
    description?: string;
  };
  layout?: {
    width: number;
    breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  };
  styling?: {
    textAlign?: 'left' | 'center' | 'right';
    fontSize?: string;
    fontWeight?: 'normal' | 'bold' | '100' | '200' | '300' | '400' | '500' | '600' | '700' | '800' | '900';
    color?: string;
    backgroundColor?: string;
    padding?: string;
    marginTop?: string;
    marginBottom?: string;
    headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  };
  pricing?: {
    enabled: boolean;
    options: { [optionValue: string]: number };
  };
  htmlContent?: {
    content: string;
    enableVariables: boolean;
    systemVariables?: string[];
    fieldVariables?: string[];
  };
  validation?: {
    min?: number;
    max?: number;
    pattern?: string;
    custom?: {
      enabled: boolean;
      rules: {
        id: string;
        type: 'compare_dates' | 'compare_numbers' | 'min_length' | 'max_length' | 'custom_pattern' | 'dependent_value';
        compareField?: string;
        compareOperator?: 'greater_than' | 'less_than' | 'greater_equal' | 'less_equal' | 'equal' | 'not_equal';
        value?: string | number;
        message: string;
        dependentValue?: string;
      }[];
    };
  };
}

interface FormData {
  id: number | string;
  title: string;
  description: string;
  fields: FormField[];
  isActive: boolean;
  customUrl: string;
  settings: {
    requirePayment?: boolean;
    amount?: number;
    paymentAmount?: number;
    redirectUrl?: string;
    allowMultipleSubmissions?: boolean;
    notifications?: {
      enableEmail: boolean;
      enableSMS: boolean;
      emailField?: string;
      phoneField?: string;
      emailTemplate?: {
        subject: string;
        body: string;
      };
      smsTemplate?: {
        message: string;
      };
    };
  };
}

interface FormValues {
  [key: string]: any;
}

const PublicFormView: React.FC = () => {
  const { customUrl } = useParams<{ customUrl: string }>();
  const navigate = useNavigate();

  const [form, setForm] = useState<FormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formIsNotActive, setFormIsNotActive] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [calculatedAmount, setCalculatedAmount] = useState<number>(0);
  const [pricingBreakdown, setPricingBreakdown] = useState<any[]>([]);
  const [dataRestored, setDataRestored] = useState<boolean>(false);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
  const [uploadWarning, setUploadWarning] = useState<string | null>(null);
  const [uploadWarningOpen, setUploadWarningOpen] = useState(false);

  const handleFileUpload = async (fieldName: string, file: File | null) => {
    if (!file) {
      handleInputChange(fieldName, null);
      return;
    }

    setUploadingFiles(prev => ({
      ...prev,
      [fieldName]: true
    }));

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/file-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Store the file path from the response
      if (response.data.filePath) {
        setUploadWarning(null);
        handleInputChange(fieldName, response.data.filePath);
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      const warningMessage = error?.response?.data?.message || 'Failed to upload file. Please try again.';
      setUploadWarning(warningMessage);
      setUploadWarningOpen(true);
    } finally {
      setUploadingFiles(prev => ({
        ...prev,
        [fieldName]: false
      }));
    }
  };

  const handleUploadWarningClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setUploadWarningOpen(false);
  };

  const fetchForm = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms/url/${customUrl}`);
      
      if (response.data && response.data.isActive) {
        setForm(response.data);
        setFormIsNotActive(false);
        
        // Try to restore form data from session storage first
        const savedFormData = sessionStorage.getItem(`formData_${customUrl}`);
        let initialValues: FormValues = {};
        let hasRestoredData = false;
        
        if (savedFormData) {
          try {
            const parsedData = JSON.parse(savedFormData);
            initialValues = parsedData;
            hasRestoredData = Object.keys(parsedData).some(key => {
              const value = parsedData[key];
              return value !== '' && (Array.isArray(value) ? value.length > 0 : true);
            });
          } catch (e) {
            console.warn('Failed to parse saved form data:', e);
          }
        }
        
        // Initialize form values only for input fields, preserving saved values
        response.data.fields.forEach((field: FormField) => {
          // Skip text/styling fields as they don't need form values
          if (!['heading', 'paragraph', 'divider', 'html'].includes(field.type)) {
            // Only set default value if no saved value exists
            if (initialValues[field.name] === undefined) {
              if (field.type === 'checkbox') {
                initialValues[field.name] = [];
              } else {
                initialValues[field.name] = '';
              }
            }
          }
        });
        
        setFormValues(initialValues);
        setDataRestored(hasRestoredData);
      } else if (!response.data.isActive) {
        setError('This form has been deactivated.');
        setFormIsNotActive(true);
      } else {
        setError('This form is not available or has been deactivated.');
      }
    } catch (error: any) {
      console.error('Error fetching form:', error);
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        setError('Failed to load form. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  }, [customUrl]);

  useEffect(() => {
    if (customUrl) {
      fetchForm();
    }
  }, [customUrl, fetchForm]);

  const validateForm = (): boolean => {
    if (!form) return false;

    // Get visible fields for validation
    const visibleFields = form.fields.filter(field => isFieldVisible(field));
    
    // Use the comprehensive validation function
    const validationErrors = validateFormFields(visibleFields, formValues);
    
    // Group errors by field name for display
    const groupedErrors = groupErrorsByField(validationErrors);
    
    // Convert to the format expected by the component
    const errorMessages: { [key: string]: string } = {};
    Object.keys(groupedErrors).forEach(fieldName => {
      const fieldErrors = groupedErrors[fieldName];
      if (fieldErrors.length > 0) {
        // Join multiple error messages with newlines
        errorMessages[fieldName] = fieldErrors.map(err => err.message).join('\n');
      }
    });

    setValidationErrors(errorMessages);
    return validationErrors.length === 0;
  };

  const handleInputChange = (fieldName: string, value: any) => {
    const newFormValues = {
      ...formValues,
      [fieldName]: value
    };
    
    setFormValues(newFormValues);
    
    // Save form data to session storage for recovery
    if (customUrl) {
      sessionStorage.setItem(`formData_${customUrl}`, JSON.stringify(newFormValues));
    }
    
    // Clear validation error when user starts typing
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldName]: ''
      }));
    }
  };

  const handleCheckboxChange = (fieldName: string, option: string, checked: boolean) => {
    const currentValues = formValues[fieldName] || [];
    let newValues;
    
    if (checked) {
      newValues = [...currentValues, option];
    } else {
      newValues = currentValues.filter((val: string) => val !== option);
    }
    
    handleInputChange(fieldName, newValues);
  };

  // Function to clear form data
  const clearFormData = () => {
    if (!form) return;
    
    const initialValues: FormValues = {};
    form.fields.forEach((field: FormField) => {
      if (!['heading', 'paragraph', 'divider', 'html'].includes(field.type)) {
        if (field.type === 'checkbox') {
          initialValues[field.name] = [];
        } else {
          initialValues[field.name] = '';
        }
      }
    });
    
    setFormValues(initialValues);
    setValidationErrors({});
    setDataRestored(false);
    
    // Clear saved data from session storage
    if (customUrl) {
      sessionStorage.removeItem(`formData_${customUrl}`);
    }
  };

  const isFieldVisible = (field: FormField): boolean => {
    // First check field visibility permissions (this is the security layer)
    const visibleFields = filterFieldsByVisibility([field], null, 'view'); // null = public user
    if (visibleFields.length === 0) {
      return false; // Field is not visible to public users
    }

    // Then check conditional logic
    if (!field.conditional || !field.conditional.dependsOn) {
      return true; // No conditional logic, show if visibility allows
    }

    const dependentField = form?.fields.find(f => f.id === field.conditional!.dependsOn);
    if (!dependentField) {
      return true; // Dependent field not found, show by default
    }

    const dependentValue = formValues[dependentField.name];
    
    // Handle different field types
    if (dependentField.type === 'checkbox') {
      // For checkboxes, check if any of the selected values match showWhen
      const selectedValues = Array.isArray(dependentValue) ? dependentValue : [];
      return field.conditional.showWhen.some(showValue => 
        selectedValues.includes(showValue)
      );
    } else {
      // For other field types, check if the value matches showWhen
      return field.conditional.showWhen.includes(dependentValue);
    }
  };

  // Calculate dynamic pricing based on current form values
  const calculatePricing = useCallback(async () => {
    if (!form || !form.settings?.requirePayment) {
      setCalculatedAmount(0);
      setPricingBreakdown([]);
      return;
    }

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/forms/${form.id}/calculate-amount`,
        { data: formValues }
      );
      
      setCalculatedAmount(response.data.calculatedAmount);
      setPricingBreakdown(response.data.breakdown || []);
    } catch (error) {
      console.error('Error calculating pricing:', error);
      // Fallback to base amount
      setCalculatedAmount(form.settings?.amount || 0);
      setPricingBreakdown([]);
    }
  }, [form, formValues]);

  // Recalculate pricing when form values change
  useEffect(() => {
    if (form) {
      calculatePricing();
    }
  }, [form, formValues, calculatePricing]);

  // Save form data to session storage whenever form values change
  useEffect(() => {
    if (customUrl && Object.keys(formValues).length > 0) {
      sessionStorage.setItem(`formData_${customUrl}`, JSON.stringify(formValues));
    }
  }, [formValues, customUrl]);

  // Cleanup effect to remove session storage data when component unmounts
  // or when navigating to a different form
  useEffect(() => {
    return () => {
      // Only cleanup if we're not navigating to payment (preserve data for back navigation)
      const isNavigatingToPayment = window.location.pathname.includes('/payment/');
      if (!isNavigatingToPayment && customUrl) {
        // Don't remove data immediately, let it persist for potential back navigation
        // Data will be cleaned up after successful form submission instead
      }
    };
  }, [customUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // If payment is required, navigate to payment page
      if (form?.settings.requirePayment && calculatedAmount > 0) {
        // Store form data temporarily for payment process (keep existing storage)
        sessionStorage.setItem('pendingFormSubmission', JSON.stringify({
          formId: form.id,
          customUrl: form.customUrl,
          formData: formValues
        }));
        
        navigate(`/payment/${form.customUrl}`, { 
          state: { 
            formData: formValues, 
            amount: calculatedAmount, // Use calculated amount instead of base amount
            formTitle: form.title,
            formId: form.id
          } 
        });
      } else {
        // Submit form directly
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/forms/${form?.id}/submit`,
          { data: formValues }
        );

        if (response.data.success) {
          // Clear saved form data only after successful submission
          if (customUrl) {
            sessionStorage.removeItem(`formData_${customUrl}`);
            sessionStorage.removeItem('pendingFormSubmission');
          }
          
          navigate(`/success/${form?.customUrl}`, { 
            state: { 
              submissionId: response.data.submissionId,
              uniqueId: response.data.uniqueId,
              editCode: response.data.editCode,
              editUrl: response.data.editUrl,
              viewUrl: response.data.viewUrl,
              message: 'Form submitted successfully!'
            } 
          });
        }
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setError('Failed to submit form. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field: FormField) => {
    const hasError = !!validationErrors[field.name];
    const errorMessage = validationErrors[field.name];
    
    // Helper function to render error messages with line breaks
    const renderErrorMessage = (message: string) => {
      if (!message) return null;
      return message.split('\n').map((line, index) => (
        <Typography key={index} variant="caption" color="error" display="block">
          {line}
        </Typography>
      ));
    };

    switch (field.type) {
      case 'heading':
        return (
          <Typography 
            key={field.id}
            variant={field.styling?.headingLevel || 'h2'}
            sx={{
              textAlign: field.styling?.textAlign || 'left',
              fontSize: field.styling?.fontSize,
              fontWeight: field.styling?.fontWeight || 'normal',
              color: field.styling?.color,
              backgroundColor: field.styling?.backgroundColor,
              padding: field.styling?.padding,
              marginTop: field.styling?.marginTop,
              marginBottom: field.styling?.marginBottom,
            }}
          >
            {field.label || 'Heading Text'}
          </Typography>
        );

      case 'paragraph':
        return (
          <Typography 
            key={field.id}
            variant="body1"
            sx={{
              textAlign: field.styling?.textAlign || 'left',
              fontSize: field.styling?.fontSize,
              fontWeight: field.styling?.fontWeight || 'normal',
              color: field.styling?.color,
              backgroundColor: field.styling?.backgroundColor,
              padding: field.styling?.padding,
              marginTop: field.styling?.marginTop,
              marginBottom: field.styling?.marginBottom,
            }}
          >
            {field.label || 'Paragraph text content goes here...'}
          </Typography>
        );

      case 'divider':
        return (
          <Divider 
            key={field.id}
            sx={{
              backgroundColor: field.styling?.color,
              marginTop: field.styling?.marginTop || '16px',
              marginBottom: field.styling?.marginBottom || '16px',
            }}
          />
        );

      case 'html':
        return (
          <HtmlContentField
            key={field.id}
            field={field}
            formData={form}
            submissionData={null}
            isPreview={false}
          />
        );

      case 'text':
      case 'email':
      case 'url':
        return (
          <TextField
            key={field.id}
            fullWidth
            label={field.label}
            type={field.type}
            placeholder={field.placeholder}
            value={formValues[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
            error={hasError}
            helperText={errorMessage}
            margin="normal"
          />
        );

      case 'tel':
        return (
          <PhoneInput
            key={field.id}
            label={field.label}
            placeholder={field.placeholder}
            value={formValues[field.name] || ''}
            onChange={(number, fullNumber) => handleInputChange(field.name, fullNumber)}
            required={field.required}
            error={hasError}
            helperText={errorMessage}
            margin="normal"
            fullWidth
          />
        );

      case 'textarea':
        return (
          <TextField
            key={field.id}
            fullWidth
            multiline
            rows={4}
            label={field.label}
            placeholder={field.placeholder}
            value={formValues[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
            error={hasError}
            helperText={errorMessage}
            margin="normal"
          />
        );

      case 'number':
        return (
          <TextField
            key={field.id}
            fullWidth
            type="number"
            label={field.label}
            placeholder={field.placeholder}
            value={formValues[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
            error={hasError}
            helperText={errorMessage}
            margin="normal"
          />
        );

      case 'select':
        return (
          <FormControl key={field.id} fullWidth margin="normal" error={hasError}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={formValues[field.name] || ''}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              label={field.label}
              required={field.required}
            >
              {field.options?.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
            {hasError && (
              <Box sx={{ ml: 2, mt: 0.5 }}>
                {renderErrorMessage(errorMessage)}
              </Box>
            )}
          </FormControl>
        );

      case 'radio':
        return (
          <FormControl key={field.id} component="fieldset" margin="normal" error={hasError}>
            <Typography variant="subtitle1" gutterBottom>
              {field.label}
              {field.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            <RadioGroup
              value={formValues[field.name] || ''}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
            >
              {field.options?.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
            </RadioGroup>
            {hasError && (
              <Box>
                {renderErrorMessage(errorMessage)}
              </Box>
            )}
          </FormControl>
        );

      case 'checkbox':
        return (
          <Box key={field.id} sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle1" gutterBottom>
              {field.label}
              {field.required && <span style={{ color: 'red' }}>*</span>}
            </Typography>
            {field.options?.map((option, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={(formValues[field.name] || []).includes(option)}
                    onChange={(e) => handleCheckboxChange(field.name, option, e.target.checked)}
                  />
                }
                label={option}
              />
            ))}
            {hasError && (
              <Box>
                {renderErrorMessage(errorMessage)}
              </Box>
            )}
          </Box>
        );

      case 'date':
        return (
          <TextField
            key={field.id}
            fullWidth
            type="date"
            label={field.label}
            value={formValues[field.name] || ''}
            onChange={(e) => handleInputChange(field.name, e.target.value)}
            required={field.required}
            error={hasError}
            helperText={errorMessage}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        );
        case 'file':
          return (
            <Box key={field.id} sx={{ mt: 2, mb: 1 }}>
              <Typography variant="subtitle1" gutterBottom>
                {field.label}
                {field.required && <span style={{ color: 'red' }}>*</span>}
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  width: '100%',
                  border: '1px solid rgba(0, 0, 0, 0.23)',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'background.paper'
                }}
              >
                <input
                  type="file"
                  id={`file-input-${field.id}`}
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileUpload(field.name, e.target.files?.[0] || null)}
                  disabled={uploadingFiles[field.name]}
                />
                <Button
                  variant="outlined"
                  component="label"
                  htmlFor={`file-input-${field.id}`}
                  sx={{
                    border: 0,
                    borderRight: '1px solid rgba(0, 0, 0, 0.23)',
                    borderRadius: 0,
                    bgcolor: 'grey.100',
                    color: 'text.primary',
                    px: 2,
                    minWidth: 110,
                    flexShrink: 0,
                    '&:hover': {
                      border: 0,
                      borderRight: '1px solid rgba(0, 0, 0, 0.23)',
                      bgcolor: 'grey.200'
                    }
                  }}
                  disabled={uploadingFiles[field.name]}
                >
                  {uploadingFiles[field.name] ? 'Uploading...' : 'Browse...'}
                </Button>
                <Box sx={{ display: 'flex', alignItems: 'center', px: 1.5, minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {typeof formValues[field.name] === 'string' 
                      ? formValues[field.name]
                      : (formValues[field.name]?.name || 'No file selected.')}
                  </Typography>
                </Box>
              </Box>
              {hasError && (
                <Box>
                  {renderErrorMessage(errorMessage)}
                </Box>
              )}
            </Box>
          );

      default:
        return null;
    }
  };

  const renderFormDescription = (description: string) => {
    const sanitizedDescription = sanitizeHtmlContent(description);

    if (!sanitizedDescription) {
      return null;
    }

    return (
      <Box
        sx={{
          color: 'text.secondary',
          mb: 2,
          '& p': {
            margin: '0 0 1em 0',
            '&:last-of-type': { marginBottom: 0 }
          },
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            margin: '1em 0 0.5em 0',
            '&:first-of-type': { marginTop: 0 }
          },
          '& ul, & ol': {
            margin: '1em 0',
            paddingLeft: '2em'
          },
          '& a': {
            color: 'primary.main'
          },
          '& img': {
            maxWidth: '100%',
            height: 'auto'
          }
        }}
        dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
      />
    );
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (notFound) {
    return (
      <NotFound
        title="Form Not Found"
        message="The form you're looking for doesn't exist or the URL is incorrect. Please check the URL and try again."
        showHomeButton={true}
        customAction={{
          label: "Try Again",
          onClick: () => window.location.reload()
        }}
      />
    );
  }
  if (formIsNotActive) {
    return (
      <NotFound
        title="Form Not Active"
        message="This form has been deactivated and is no longer accepting submissions."
        showHomeButton={true}
        customAction={{
          label: "Download Submissions",
          onClick: () => {
            // show input of submission code and download submissions
            const submissionCode = prompt("Enter the submission code to download submissions:");
            if (submissionCode) {
              console.log('Downloading submissions with code:', submissionCode);
              window.location.href = `/pdf/${submissionCode}`;
            }
          }
        }}
      />
    );
  }


  if (error) {
    return (
      <Container maxWidth="md">
        <Box textAlign="center" py={8}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button 
            variant="contained" 
            onClick={() => window.location.href = '/'}
          >
            Go to Homepage
          </Button>
        </Box>
      </Container>
    );
  }

  if (!form) {
    return (
      <NotFound
        title="Form Not Available"
        message="This form is currently not available or has been removed."
        showHomeButton={true}
      />
    );
  }

  return (
    <>
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {form.title}
        </Typography>
        
        {form.description && renderFormDescription(form.description)}

        {dataRestored && (
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => setDataRestored(false)}
              >
                Dismiss
              </Button>
            }
          >
            Your previously entered form data has been restored. You can continue from where you left off.
          </Alert>
        )}

        {uploadWarning && (
          <Alert severity="warning" sx={{ mb: 3 }} onClose={() => setUploadWarning(null)}>
            {uploadWarning}
          </Alert>
        )}

        {form.settings.requirePayment && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Payment Required: ৳{(calculatedAmount || 0).toFixed(2)}
              </Typography>
              
              {pricingBreakdown && pricingBreakdown.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {form.settings.amount !== undefined && (
                    <Typography variant="body2">
                      Base Price: ৳{(form.settings.amount || 0).toFixed(2)}
                    </Typography>
                  )}
                  
                  {pricingBreakdown.map((item, index) => (
                    <Box key={index} sx={{ ml: 2, mt: 0.5 }}>
                      <Typography variant="body2" color="textSecondary">
                        {item.fieldLabel}:
                      </Typography>
                      {item.selectedOptions.map((option: any, optIdx: number) => (
                        <Typography key={optIdx} variant="body2" sx={{ ml: 2 }}>
                          • {option.option}: +৳{option.price.toFixed(2)}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                  
                  {calculatedAmount !== (form.settings.amount || 0) && (
                    <Divider sx={{ my: 1 }} />
                  )}
                  
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Total: ৳{(calculatedAmount || 0).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        <form onSubmit={handleSubmit}>
          <Box 
            sx={{ 
              display: 'flex',
              flexWrap: 'wrap',
              margin: '-8px', // Negative margin to offset gap
              '& > *': {
                padding: '8px', // Padding instead of gap for better control
                boxSizing: 'border-box'
              }
            }}
          >
            {form.fields
              .sort((a, b) => a.order - b.order)
              .filter((field) => isFieldVisible(field))
              .map((field) => (
                <Box 
                  key={field.id}
                  sx={{ 
                    width: field.layout?.width ? `${(field.layout.width / 12) * 100}%` : '100%',
                    '@media (max-width: 600px)': {
                      width: '100%'
                    }
                  }}
                >
                  {renderField(field)}
                </Box>
              ))}
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mt: 4, textAlign: 'center', display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={submitting}
              sx={{ minWidth: 200 }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : form.settings.requirePayment && calculatedAmount > 0 ? (
                `Proceed to Payment (৳${(calculatedAmount || 0).toFixed(2)})`
              ) : (
                'Submit Form'
              )}
            </Button>
            
            <Button
              type="button"
              variant="outlined"
              size="large"
              onClick={clearFormData}
              disabled={submitting}
              sx={{ minWidth: 120 }}
            >
              Clear Form
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
    <Snackbar
      open={uploadWarningOpen}
      autoHideDuration={4000}
      onClose={handleUploadWarningClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert onClose={handleUploadWarningClose} severity="warning" sx={{ width: '100%' }}>
        {uploadWarning}
      </Alert>
    </Snackbar>
    </>
  );
};

export default PublicFormView;
